import { ArrowLeft, Pencil, UserCog } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import { SendTestEmailButton } from '@/components/features/email/SendTestEmailButton'
import { Button } from '@/components/ui/Button'
import { DetailPageError, DetailPageLoading } from '@/components/ui/DetailPage'
import { SuccessBanner } from '@/components/ui/SuccessBanner'
import { DISTRICT_MANAGER_LEVEL, TEMP_CLERK_LEVEL } from '@/constants/pos-user-levels'
import { useDistricts, useLocations } from '@/hooks/use-locations'
import { useWriteAccess } from '@/hooks/use-permissions'
import { usePosUser, usePosUserTypes } from '@/hooks/use-pos-users'
import { scrollToTop } from '@/lib/ui/scroll'
import { type Location } from '@/models/domain/location'
import { type PosUser, type PosUserTypeSummary } from '@/models/domain/pos-user'
import { formatEpochDateTime } from '@/utils/table-formatting'

function DetailField({
  label,
  value,
}: {
  label: string
  value: string | number | null | undefined
}) {
  const display = value === null || value === undefined || value === '' ? '—' : String(value)
  return (
    <div>
      <p className="text-xs font-normal text-gray-500 dark:text-gray-400 uppercase tracking-wider">
        {label}
      </p>
      <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{display}</p>
    </div>
  )
}

function findTypeName(userTypes: PosUserTypeSummary[], level: number): string {
  const match = userTypes.find((t) => t.level === level)
  return match ? match.name : `Level ${String(level)}`
}

function StatusBadge({ active }: { active: boolean }) {
  const classes = active
    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${classes}`}>
      {active ? 'Active' : 'Inactive'}
    </span>
  )
}

function IdentityCard({
  user,
  typeName,
  districtName,
  canWrite,
  onEdit,
}: {
  user: PosUser
  typeName: string
  districtName: string | undefined
  canWrite: boolean
  onEdit: () => void
}) {
  const isTempClerk = user.level === TEMP_CLERK_LEVEL
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="p-6 space-y-6">
        <div className="flex items-start justify-between gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Identity & Access
            </h3>
            <div className="mt-2 flex items-center gap-2">
              <StatusBadge active={user.active} />
            </div>
          </div>
          {canWrite && (
            <Button
              onClick={onEdit}
              variant="secondary"
              size="sm"
              aria-label="Edit POS User"
              title="Edit POS User"
            >
              <Pencil className="w-4 h-4" />
              Edit
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <DetailField label="First name" value={user.first_name} />
          <DetailField label="Last name" value={user.last_name} />
          <DetailField label="Email" value={user.email} />
          <DetailField label="User type" value={typeName} />
          <DetailField label="District" value={districtName ?? user.district_id} />
          <DetailField label="Location" value={user.location_id} />
          <DetailField label="PIN" value={user.pin ? 'Set' : 'Not set'} />
          <DetailField label="Last modified" value={formatEpochDateTime(user.modified)} />
          {isTempClerk && <DetailField label="Expires" value={formatEpochDateTime(user.expires)} />}
          {isTempClerk && (
            <DetailField label="Expires end of shift" value={user.expires_eos ? 'Yes' : 'No'} />
          )}
        </div>
      </div>
    </div>
  )
}

function DistrictManagerActions({ email }: { email: string | undefined }) {
  if (!email) return null
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="p-6 space-y-3">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          District Manager Tools
        </h3>
        <SendTestEmailButton email={email} label={`Send test email to ${email}`} />
      </div>
    </div>
  )
}

function DistrictLocationsCard({
  districtId,
  districtName,
}: {
  districtId: string
  districtName: string | undefined
}) {
  const { data: locations = [] } = useLocations()
  const districtLocations = locations.filter((loc: Location) => loc.district_id === districtId)

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="p-6 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            District: {districtName ?? districtId}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Locations in this district
          </p>
        </div>
        {districtLocations.length === 0 ? (
          <p className="text-sm italic text-gray-500 dark:text-gray-400">
            No locations found in this district
          </p>
        ) : (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800">
                  <th className="px-3 py-2 text-left text-xs font-normal text-gray-500 dark:text-gray-400 uppercase">
                    ID
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-normal text-gray-500 dark:text-gray-400 uppercase">
                    Name
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-normal text-gray-500 dark:text-gray-400 uppercase">
                    City
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-normal text-gray-500 dark:text-gray-400 uppercase">
                    State
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {districtLocations.map((loc) => (
                  <tr key={loc.id} className="bg-white dark:bg-gray-900">
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100 font-mono">
                      {loc.id}
                    </td>
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                      {loc.name ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                      {loc.city ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                      {loc.state ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function PageHeader({ user }: { user: PosUser }) {
  return (
    <div className="mb-6">
      <Link
        to="/admin/pos-users"
        className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to POS users
      </Link>
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
          <UserCog className="w-6 h-6 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">POS User Detail</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {user.first_name} {user.last_name} (#{user.id})
          </p>
        </div>
      </div>
    </div>
  )
}

function useSuccessBannerFromRouterState() {
  const location = useLocation()
  const navigate = useNavigate()
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const needsScroll = useRef(false)

  useEffect(() => {
    const state = location.state as { success?: string } | null
    if (!state?.success) return
    setSuccessMessage(state.success)
    needsScroll.current = true
    navigate(location.pathname, { replace: true, state: {} })
  }, [location.state, location.pathname, navigate])

  return { successMessage, setSuccessMessage, needsScroll }
}

function LoadedContent({
  user,
  canWrite,
  onEdit,
}: {
  user: PosUser
  canWrite: boolean
  onEdit: () => void
}) {
  const { data: userTypes = [] } = usePosUserTypes()
  const { data: districts = [] } = useDistricts()

  const typeName = findTypeName(userTypes, user.level)
  const districtName = user.district_id
    ? districts.find((d) => d.id === user.district_id)?.name
    : undefined
  const isDistrictManager = user.level === DISTRICT_MANAGER_LEVEL

  return (
    <div className="space-y-6">
      <IdentityCard
        user={user}
        typeName={typeName}
        districtName={districtName}
        canWrite={canWrite}
        onEdit={onEdit}
      />
      {isDistrictManager && <DistrictManagerActions email={user.email} />}
      {user.district_id && (
        <DistrictLocationsCard districtId={user.district_id} districtName={districtName} />
      )}
    </div>
  )
}

export function PosUserDetailPage() {
  const { id = '' } = useParams<{ id: string }>()
  const numericId = Number(id)
  const navigate = useNavigate()
  const canWrite = useWriteAccess('cs_pos_users_w')
  const { successMessage, setSuccessMessage, needsScroll } = useSuccessBannerFromRouterState()

  const { data: user, isLoading, error: fetchError } = usePosUser(numericId)

  useEffect(() => {
    if (needsScroll.current && user) {
      needsScroll.current = false
      scrollToTop()
    }
  }, [user, needsScroll])

  if (!Number.isFinite(numericId) || numericId <= 0)
    return (
      <DetailPageError
        entityLabel="POS user"
        backTo="/admin/pos-users"
        backLabel="Back to POS users"
        error={null}
      />
    )
  if (isLoading) return <DetailPageLoading entityLabel="POS user" />
  if (fetchError || !user)
    return (
      <DetailPageError
        entityLabel="POS user"
        backTo="/admin/pos-users"
        backLabel="Back to POS users"
        error={fetchError}
      />
    )

  const goEdit = () => {
    navigate(`/admin/pos-users/${String(user.id)}/edit`)
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <PageHeader user={user} />
      <SuccessBanner
        message={successMessage}
        onDismiss={() => {
          setSuccessMessage(null)
        }}
      />
      <LoadedContent user={user} canWrite={canWrite} onEdit={goEdit} />
    </div>
  )
}
